import React, { useState, useRef } from 'react';
import Typography from '../../../basic/Typography';
import './AudioPreview.css';

interface AudioPreviewProps {
  src: string;
  fileName: string;
  fileSize: number;
}

export const AudioPreview: React.FC<AudioPreviewProps> = ({
  src,
  fileName,
  fileSize,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  return (
    <div className="audio-preview">
      <div className="preview-header">
        <Typography variant="h4" gutterBottom={false}>
          Audio Preview
        </Typography>
        <div className="file-info">
          <span className="file-name">{fileName}</span>
          <span className="file-size">({formatFileSize(fileSize)})</span>
        </div>
      </div>

      <div className="audio-player">
        <audio
          ref={audioRef}
          src={src}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
        />

        <div className="audio-controls">
          <button
            type="button"
            className={`play-pause-btn ${isPlaying ? 'playing' : ''}`}
            onClick={handlePlayPause}
          >
            {isPlaying ? '⏸️' : '▶️'}
          </button>

          <div className="time-display">
            <Typography variant="body2" gutterBottom={false}>
              {formatTime(currentTime)}
            </Typography>
            <Typography variant="body2" gutterBottom={false}>
              /
            </Typography>
            <Typography variant="body2" gutterBottom={false}>
              {formatTime(duration)}
            </Typography>
          </div>
        </div>

        <div className="progress-container">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="progress-bar"
          />
        </div>
      </div>
    </div>
  );
};
